import React, { useState, useEffect } from "react";
import { addChildren } from "../../services/Api";
import { useAuth } from "../../services/AuthContext";
import { useToast } from '../../services/ToastContext';
import PopupWrapper from "../../components/PopupWrapper";
import { useRefresh } from "../../services/RefreshContext";
import Tooltip from "../../components/Tooltip";

const AddChildForm = ({ 
  isOpen, 
  onClose, 
  guardianID, 
  familyName, 
  email, 
  primaryRate, 
  secondaryRate, 
  cappedPrimaryInterestValue, 
  doCreateToken 
}) => {
  const { checkAuth } = useAuth();
  const { showToast } = useToast();
  const { triggerRefresh } = useRefresh();

  const [firstName, setFirstName] = useState("");
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [childrenList, setChildrenList] = useState([]);
  const [inputErrors, setInputErrors] = useState({
    inputFirstName: "",
  });

  useEffect(() => {
    
    // Reset form when closed
    return () => {
      if (!isOpen) {
        setFirstName("");
        setChildrenList([]);
        setAlreadySubmitted(false);
        setError("");
        setInputErrors({
          inputFirstName: "",
        });
      }
    };
  }, [isOpen]);

  const validateAddChildForm = () => {
    let valid = true;
    const newErrors = {};

    if (!firstName.trim()) {
      newErrors.inputFirstName = "Child first name is required";
      valid = false;
    }

    setInputErrors(newErrors);
    return valid;
  };

  const handleDoneButton = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await addChildren(
        childrenList, 
        guardianID, 
        familyName, 
        email, 
        primaryRate, 
        doCreateToken,
        secondaryRate, 
        cappedPrimaryInterestValue,
      );

      if (response.token) {
        localStorage.setItem("token", response.token);
        await checkAuth();
      }

      showToast((childrenList?.length > 1 ? "Children" : "Child") + " Added");
      triggerRefresh();
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validateAddChildForm()) {
      setIsLoading(false);
      return;
    }

    const child = { FirstName: firstName };
    setChildrenList(prevChildren => [...prevChildren, child]);

    setFirstName("");
    setAlreadySubmitted(true);
    setIsLoading(false);
  };

  const handleRemoveChild = (indexToRemove) => {
    const updatedList = childrenList.filter(
      (_, index) => index !== indexToRemove
    );
    setChildrenList(updatedList);
    setAlreadySubmitted(updatedList.length > 0);
  };

  if (!isOpen) return null;

  return (
    <PopupWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Add Your Child!"
      isSecondary={true}
    >
      <form className="user">
        <div style={{ display: "flex" }}>
          <input
            type="text"
            className="form-control form-control-user"
            id="firstNameInputAddChild"
            placeholder="First Name"
            style={{ width: "90%" }}
            onChange={(e) => setFirstName(e.target.value)}
            value={firstName}
          />

          <button
            className={`confirm-btn ${isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            onClick={handleAddChild}
            style={{
              width: "11%",
              fontSize: "20px",
              margin: "0px",
              marginLeft: "15px",
            }}
          >
            <Tooltip
              content={<i className="fas fa-plus fa-lg green" />}
              description="Add"
              isCustom={true}
            />
          </button>
        </div>
        {inputErrors.inputFirstName && (
          <p className="text-danger">{inputErrors.inputFirstName}</p>
        )}
        <br />
        {error && <p className="text-danger">{error}</p>}

        {childrenList && childrenList.length > 0 && (
          <div className="mb-4 p-3 bg-light rounded shadow-sm">
            <h5 className="text-gray-700 mb-3 text-center">
              Hit done to add:
            </h5>
            <div className="d-flex flex-wrap justify-content-center gap-2">
              {childrenList.map((child, index) => (
                <div
                  key={index}
                  className="badge green-color text-white p-2 m-1 rounded-pill fs-5"
                  style={{
                    letterSpacing: "0.05em",
                    fontWeight: "normal",
                  }}
                >
                  {child.FirstName}
                  <button
                    type="button"
                    onClick={() => handleRemoveChild(index)}
                    className="btn btn-sm btn-light text-dark ms-2 py-0 px-2"
                    style={{
                      borderRadius: "100%",
                      fontWeight: "bold",
                      lineHeight: "1.7",
                      marginLeft: "10px",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="d-flex justify-content-center gap-3">
          <button
            onClick={handleDoneButton}
            className={`longer-btn p-3 pr-5 pl-5 blue-color ${!alreadySubmitted 
              ? "opacity-50 cursor-not-allowed hover:longer-btn:hover" 
              : ""
            }`}
            style={!alreadySubmitted ? { pointerEvents: "none" } : {}}
            disabled={!alreadySubmitted || isLoading}
          >
            {doCreateToken ? "Finish" : "Done"}
          </button>
        </div>

        {!doCreateToken && (
          <div className="text-center mt-3">
            <button 
              className="editing-btn p-2"
              onClick={() => onClose()}
            >
              Cancel
            </button>
          </div>
        )}
      </form>
    </PopupWrapper>
  );
};

export default AddChildForm;